import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createElementBlock as _createElementBlock, Transition as _Transition, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 4,
  class: "breadcrumbs-container"
}
const _hoisted_2 = {
  key: 5,
  class: "search-container"
}
const _hoisted_3 = {
  key: 6,
  class: "header"
}
const _hoisted_4 = {
  key: 0,
  class: "loading-overlay"
}
const _hoisted_5 = {
  key: 0,
  class: "error-message"
}
const _hoisted_6 = {
  key: 0,
  class: "info-message"
}
const _hoisted_7 = {
  key: 0,
  class: "footer"
}
const _hoisted_8 = {
  key: 1,
  class: "preview-container"
}

import { ref, computed, watch, toRefs, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import DirectoryToolbar from './DirectoryToolbar.vue'
import InputSearch from '@/design-system/molecules/InputSearch.vue'
import FilePreviewModal from './FilePreviewModal.vue'
import BreadcrumbFiles from './BreadcrumbFiles.vue'
import { isError, type DirectoryItem } from './types'
import BasicTextInputModal from './BasicTextInputModal.vue'
import DirectoryListView from './list-view/DirectoryListView.vue'
import EditModal from './EditModal.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import DirectoryItemSelectionModal from './DirectoryItemSelectionModal.vue'
import { CreateNewAction } from './actions/createNewAction'
import { MoveAction } from './actions/moveAction'
import { EditAction } from './actions/editAction'
// import FileConflictModal from './FileConflictModal.vue'
import UploadModal from './UploadModal.vue'
import { useGeneralStore } from '@/pinia'
import ConnectionPopup from './ConnectionPopup.vue'
import { CoreEntityDto } from '@/interfaces/core.interface'
import InputFileDropBase from './InputFileDropBase.vue'
import SettingsModal from './SettingsModal.vue'
import { useActions } from './actionStore'
import { storeToRefs } from 'pinia'
import FileMetaInfo from './FileMetaInfo.vue'
import FilePreview from './FilePreview.vue'
import { postDirectoryItem } from './files.service'
import { useFileManagerConnected } from './useFileManagerConnected'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import ConfirmDeleteAttachmentCard from '../attachments/ConfirmDeleteAttachmentCard.vue'
import { DeleteAction } from './actions/deleteAction'
import { useFilePreview } from './useFilePreview'

type Props = {
  showSearch?: boolean
  showBreadcrumbs?: boolean
  showToolbar?: boolean
  dragDropEnabled?: boolean
  multiSelectEnabled?: boolean
  fullscreen: boolean
  reversed?: boolean
  enabledActions?: string[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DirectoryNavigation',
  props: {
    showSearch: { type: Boolean },
    showBreadcrumbs: { type: Boolean },
    showToolbar: { type: Boolean },
    dragDropEnabled: { type: Boolean },
    multiSelectEnabled: { type: Boolean },
    fullscreen: { type: Boolean },
    reversed: { type: Boolean },
    enabledActions: {}
  },
  setup(__props: any) {

const props = __props
const { fullscreen } = toRefs(props)

const { t } = useI18n()

const fileManager = useFileManagerConnected()
const { newActions, itemActions, listViewActions } = storeToRefs(useActions())

const generalStore = useGeneralStore()
const { showConnectionPopup } = toRefs(generalStore)
const cameFromEdit: Ref<boolean> = ref(false)
const previewableFileSelected = ref<boolean>(false)
const previewFileFullscreen = ref<DirectoryItem<'file'> | undefined>(undefined)
const rawInputs = [
  { key: 'name', label: t('general__text__name'), required: true, type: 'textstandard' },
  { key: 'url', label: 'URL', required: true, type: 'textstandard' },
  { key: 'Beskrivning', label: t('general__text__description'), required: true, type: 'textarea' }
]

const filteredInputs = computed(() => {
  if (!selectedItem.value) return []
  if (selectedItem.value.type === 'link') {
    return rawInputs
  }
  return rawInputs.filter((input) => input.key !== 'url')
})
const showConfirmDeleteModal = computed(() => fileManager.currentCommand.value?.action === 'delete')
const showUploadModal = computed(() => fileManager.currentCommand.value?.action === 'upload')
const showNewDirectoryModal = computed(
  () => fileManager.currentCommand.value?.action === 'new-directory'
)
const showNewLinkModal = computed(() => fileManager.currentCommand.value?.action === 'new-link')
const showNewEditModal = computed(() => fileManager.currentCommand.value?.action === 'edit')
const showMoveToDirectoryModal = computed(() => fileManager.currentCommand.value?.action === 'move')
const showPreviewToDirectoryModal = computed(
  () =>
    fileManager.currentCommand.value &&
    ['open-file'].includes(fileManager.currentCommand.value.action)
)
const showSettingsModal = computed(() => fileManager.currentCommand.value?.action === 'settings')
const previewFileForModal = computed(
  () => fileManager.currentCommand.value?.items[0] as DirectoryItem<'file'>
)
const currentItemId = ref<string>()
const selectedItem = computed<DirectoryItem | undefined>(() =>
  fileManager.currentCommand.value?.items.length === 1
    ? fileManager.currentCommand.value?.items[0]
    : undefined
)
const droppedFiles = ref<FileSystemEntry[]>([])

const actionText = computed<string>(() => 'Test')

const filteredListViewActions = computed(() =>
  listViewActions.value.filter((action) =>
    props.enabledActions ? props.enabledActions.includes(action.actionName) : true
  )
)

// function conflictAction() {
//   console.debug('Conflict action triggered')
// }

const { previewType, isLoading, blobUrl, textContent, errorMessage } =
  useFilePreview(previewFileFullscreen)

watch(
  fullscreen,
  (newValue) => {
    fileManager.setFullscreen(newValue)
  },
  { immediate: true }
)
async function confirmDeleteAction() {
  if (fileManager.currentCommand.value?.items) {
    await fileManager.executeAction(new DeleteAction(fileManager.currentCommand.value?.items))
  }
}
async function handleAction(action: string, items: DirectoryItem[]) {
  fileManager.executeCommand({
    action,
    items
  })
  if (items.length === 0) {
    console.warn('Row action triggered without an item:', action)
    return
  }
  if (action === 'change-connection') {
    openConnectionPopup()
  }
  console.debug('Action clicked:', action, 'on selected items:', items)
}

function retryFetch() {
  fileManager.refresh()
}
function openConnectionPopup() {
  generalStore.setShowConnectionPopup(true)
  generalStore.setShowNoFeature(true)
}

async function handleSaveUpload() {
  fileManager.refresh()
}

async function handleSubmitNewDirectory({ name }: Record<string, string | number>) {
  const parentId = fileManager.currentDirectoryId.value
  const res = await fileManager.executeAction<{ id: string }[], undefined>(
    new CreateNewAction(
      [
        {
          name: name.toString().trim(),
          directoryId: parentId === 'root' ? undefined : parentId,
          type: 'directory'
        }
      ],
      fileManager.currentDirectoryId.value,
      postDirectoryItem
    )
  )
  if (isError(res)) {
    console.error('Failed to create new directory')
    return
  }
  currentItemId.value = res.data[0]?.id
}

async function handleSubmitNewLink({ name, url }: Record<string, string | number>) {
  const res = await fileManager.executeAction<{ id: string }[], undefined>(
    new CreateNewAction(
      [
        {
          name: name.toString().trim(),
          type: 'link',
          directoryId: fileManager.currentDirectoryId.value,
          url: url.toString().trim()
        }
      ],
      fileManager.currentDirectoryId.value,
      postDirectoryItem
    )
  )
  if (isError(res)) {
    console.error('Failed to create new link')
    return
  }
  currentItemId.value = res.data[0]?.id
}
async function handelEdit(item: DirectoryItem, edits: Record<string, string | number>) {
  fileManager.executeAction(
    new EditAction(item, {
      type: item.type,
      ...edits
    })
  )
}
async function handleNewConnection(entity: CoreEntityDto) {
  if (selectedItem.value) {
    const edits = {
      type: selectedItem.value.type,
      entityConnection: entity.id
    }
    await fileManager.executeAction(new EditAction(selectedItem.value, edits))
    await fileManager.refresh()
  }
}
function onBreadcrumbClick(option: DirectoryItem) {
  if (!option.id) {
    console.warn('Breadcrumb has no id—skipping navigation', option)
    return
  }
  fileManager.openItem(option.id)
}

function handleSubmitMoveToDirectory(targetItems: DirectoryItem[]) {
  const targetId = targetItems[0]?.id
  const actionItems = fileManager.currentCommand.value?.items
  if (actionItems) {
    fileManager.executeAction(new MoveAction(actionItems, targetId))
  }
}

const urlValidation = (url: string | number) => {
  // only allow https URLs
  const urlPattern = /^(https:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w- ./?%&=]*)?$/i
  return urlPattern.test(url.toString())
}

function openMove() {
  if (!selectedItem.value) return
  cameFromEdit.value = fileManager.currentCommand.value?.action === 'edit'
  fileManager.executeCommand({
    action: 'move',
    items: [selectedItem.value]
  })
}

function cancelMove() {
  if (cameFromEdit.value && selectedItem.value) {
    fileManager.executeCommand({
      action: 'edit',
      items: [selectedItem.value]
    })
  } else {
    fileManager.resetCommand()
  }
  cameFromEdit.value = false
}

async function handleDroppedFileItems(items: DataTransferItem[]) {
  droppedFiles.value = items
    .map((item) => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => entry !== null)
  await fileManager.executeCommand({
    action: 'upload',
    items: []
  })
}

watch(
  fileManager.selectedItems,
  (newValue) => {
    if (newValue.length === 1 && newValue[0].type === 'file') {
      previewFileFullscreen.value = newValue[0] as DirectoryItem<'file'>
      previewableFileSelected.value = true
    } else {
      previewFileFullscreen.value = undefined
      previewableFileSelected.value = false
    }
  },
  { immediate: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "directory-navigation",
    style: _normalizeStyle({ flexDirection: _ctx.reversed ? 'column-reverse' : 'column' })
  }, [
    (showConfirmDeleteModal.value)
      ? (_openBlock(), _createBlock(OverlayCard, {
          key: 0,
          class: _normalizeClass(["overlay-confirm-delete", {
        'overlay-confirm-delete__fullscreen': _unref(fullscreen)
      }]),
          onCloseOverlay: _unref(fileManager).resetCommand
        }, {
          default: _withCtx(() => [
            _createVNode(ConfirmDeleteAttachmentCard, {
              buttonText: _unref(t)('general__text__delete'),
              onCloseOverlay: _unref(fileManager).resetCommand,
              onConfirmDelete: confirmDeleteAction
            }, null, 8, ["buttonText", "onCloseOverlay"])
          ]),
          _: 1
        }, 8, ["class", "onCloseOverlay"]))
      : _createCommentVNode("", true),
    _createVNode(UploadModal, {
      "current-directory": _unref(fileManager).currentDirectory.value,
      open: showUploadModal.value,
      "file-entries": droppedFiles.value,
      onClose: _unref(fileManager).resetCommand,
      onSave: handleSaveUpload
    }, null, 8, ["current-directory", "open", "file-entries", "onClose"]),
    _createVNode(SettingsModal, {
      open: showSettingsModal.value,
      onClose: _unref(fileManager).resetCommand
    }, null, 8, ["open", "onClose"]),
    _createVNode(BasicTextInputModal, {
      title: _unref(t)('general__text__new-directory'),
      "submit-button-text": _unref(t)('general__text__create-directory'),
      open: showNewDirectoryModal.value,
      inputs: [{ key: 'name', label: _unref(t)('general__text__name'), required: true }],
      onClose: _unref(fileManager).resetCommand,
      onSubmit: handleSubmitNewDirectory
    }, null, 8, ["title", "submit-button-text", "open", "inputs", "onClose"]),
    _createVNode(BasicTextInputModal, {
      title: _unref(t)('general__text__new-link'),
      "submit-button-text": _unref(t)('general__text__create-link'),
      open: showNewLinkModal.value,
      inputs: [
        {
          key: 'name',
          label: _unref(t)('general__text__name'),
          required: true,
          validations: [
            {
              validator: (value) => value !== '',
              message: _unref(t)('general__text__missing-name')
            },
            {
              validator: (value) => typeof value === 'string' && value.length <= 255,
              message: _unref(t)('general__data__file-name-too-long')
            }
          ]
        },
        {
          key: 'url',
          label: 'URL',
          required: true,
          validations: [
            {
              validator: (value) => value !== '',
              message: `${_unref(t)('general__text__missing-url')}, ${_unref(t)(
                'general__text__valid-format'
              ).toLowerCase()} 'https://www.example.com'`
            },
            {
              validator: urlValidation,
              message: `${_unref(t)('general__text__invalid-url')}, ${_unref(t)(
                'general__text__valid-format'
              ).toLowerCase()} 'https://www.example.com'`
            }
          ],
          helpText: `${_unref(t)('general__text__valid-format')} 'https://www.example.com'`
        }
      ],
      onClose: _unref(fileManager).resetCommand,
      onSubmit: handleSubmitNewLink
    }, null, 8, ["title", "submit-button-text", "open", "inputs", "onClose"]),
    (showPreviewToDirectoryModal.value)
      ? (_openBlock(), _createBlock(FilePreviewModal, {
          key: 1,
          open: showPreviewToDirectoryModal.value,
          previewFile: previewFileForModal.value,
          showDetails: !_unref(fullscreen),
          onClose: _unref(fileManager).resetCommand,
          onAction: handleAction
        }, null, 8, ["open", "previewFile", "showDetails", "onClose"]))
      : _createCommentVNode("", true),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (_unref(showConnectionPopup))
        ? (_openBlock(), _createBlock(ConnectionPopup, {
            key: 0,
            selectedItem: selectedItem.value,
            onUpdateConnection: handleNewConnection
          }, null, 8, ["selectedItem"]))
        : _createCommentVNode("", true)
    ])),
    (selectedItem.value && filteredInputs.value)
      ? (_openBlock(), _createBlock(EditModal, {
          key: 2,
          inputs: filteredInputs.value,
          open: showNewEditModal.value,
          selectedItem: selectedItem.value,
          breadcrumbs: _unref(fileManager).breadcrumbs.value,
          onClose: _unref(fileManager).resetCommand,
          onSubmit: handelEdit,
          onOpenConnection: openConnectionPopup,
          onOpenMove: openMove
        }, null, 8, ["inputs", "open", "selectedItem", "breadcrumbs", "onClose"]))
      : _createCommentVNode("", true),
    (showMoveToDirectoryModal.value)
      ? (_openBlock(), _createBlock(DirectoryItemSelectionModal, {
          key: 3,
          title: actionText.value,
          open: showMoveToDirectoryModal.value,
          path: _unref(fileManager).breadcrumbs.value,
          "submit-button-text": _unref(t)('general__text__move-here'),
          onSubmit: handleSubmitMoveToDirectory,
          onClose: cancelMove
        }, null, 8, ["title", "open", "path", "submit-button-text"]))
      : _createCommentVNode("", true),
    (_ctx.showBreadcrumbs && _unref(fileManager).breadcrumbs.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(BreadcrumbFiles, {
            breadcrumbs: _unref(fileManager).breadcrumbs.value,
            onOnClicked: onBreadcrumbClick
          }, null, 8, ["breadcrumbs"])
        ]))
      : _createCommentVNode("", true),
    (_ctx.showSearch)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(InputSearch, {
            class: "search-container__input",
            placeholderText: _unref(t)('general__text__search-placeholder-directory'),
            modelValue: _unref(fileManager).searchTerm.value,
            "show-search-input": "",
            clearInput: "",
            onEmitSearchString: _unref(fileManager).handleSearch
          }, null, 8, ["placeholderText", "modelValue", "onEmitSearchString"])
        ]))
      : _createCommentVNode("", true),
    (_ctx.showToolbar)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(DirectoryToolbar, {
            selectedItems: _unref(fileManager).selectedItems.value,
            "new-actions": _unref(newActions),
            "bulk-actions": _unref(itemActions),
            onAction: handleAction
          }, null, 8, ["selectedItems", "new-actions", "bulk-actions"])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["content", { 'content--fullscreen': _unref(fullscreen), 'content--preview': previewableFileSelected.value }])
    }, [
      _createVNode(_Transition, null, {
        default: _withCtx(() => [
          (_unref(fileManager).isLoading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createVNode(PulseLoader, {
                  loading: true,
                  color: "#3F46D6"
                }),
                (_unref(fileManager).message.value)
                  ? (_openBlock(), _createBlock(TextBase, {
                      key: 0,
                      text: _unref(fileManager).message.value,
                      type: "paragraph",
                      style: {"margin-top":"16px"}
                    }, null, 8, ["text"]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_Transition, null, {
        default: _withCtx(() => [
          (_unref(fileManager).state.value === 'error' && !_unref(fileManager).isLoading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(TextBase, {
                  text: `Error: ${_unref(fileManager).message.value}`,
                  type: "paragraph",
                  style: {"margin-top":"16px"}
                }, null, 8, ["text"]),
                _createVNode(ButtonPrimary, {
                  "button-text": _unref(t)('general__text__reload'),
                  style: {"margin-top":"16px"},
                  onOnClicked: retryFetch
                }, null, 8, ["button-text"])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_Transition, null, {
        default: _withCtx(() => [
          (_unref(fileManager).state.value === 'message')
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createVNode(TextBase, {
                  text: _unref(fileManager).message.value,
                  type: "paragraph"
                }, null, 8, ["text"])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(InputFileDropBase, {
        class: "content__drop-zone",
        onItemsDropped: handleDroppedFileItems
      }, {
        content: _withCtx(() => [
          _createVNode(DirectoryListView, {
            headers: _unref(fileManager).sortHeaders.value,
            directoryItems: _unref(fileManager).displayedItems.value,
            actions: filteredListViewActions.value,
            currentItemId: currentItemId.value,
            "drag-drop-enabled": props.dragDropEnabled,
            "multi-select-enabled": props.multiSelectEnabled,
            showFooter: _unref(fullscreen),
            "show-headers": "",
            onAction: handleAction,
            onSelect: _unref(fileManager).handleSelectionChange,
            onDoubleClick: _unref(fileManager).openItem,
            onItemsDroppedOnTarget: _cache[0] || (_cache[0] = 
              ({ draggedItemIds, targetItemId }) =>
                _unref(fileManager).handleDrop(draggedItemIds, targetItemId)
            ),
            onSort: _unref(fileManager).handleSort,
            onLoadMoreItems: _unref(fileManager).loadMoreItems
          }, null, 8, ["headers", "directoryItems", "actions", "currentItemId", "drag-drop-enabled", "multi-select-enabled", "showFooter", "onSelect", "onDoubleClick", "onSort", "onLoadMoreItems"])
        ]),
        _: 1
      }),
      (_unref(fullscreen))
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createVNode(TextBase, {
              type: "paragraph",
              class: "footer-text",
              text: `${_unref(fileManager).currentPage.value?.totalElements ?? '-'} ${_unref(t)(
            'general__text__items'
          ).toLowerCase()}`
            }, null, 8, ["text"]),
            (
            _unref(fileManager).selectedItems.value.length > 0 &&
            _unref(fileManager).displayedItems.value.length > 0
          )
              ? (_openBlock(), _createBlock(TextBase, {
                  key: 0,
                  type: "paragraph",
                  class: "footer-text",
                  text: `${_unref(fileManager).selectedItems.value.length}  ${_unref(t)(
            'general__text__have-been-chosen'
          ).toLowerCase()}`
                }, null, 8, ["text"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      (previewableFileSelected.value && previewFileFullscreen.value && _unref(fullscreen))
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(FilePreview, {
              "preview-file": previewFileFullscreen.value,
              "preview-type": _unref(previewType),
              "file-blob-source": _unref(blobUrl),
              "text-content": _unref(textContent),
              "loading-file": _unref(isLoading),
              "file-error-text": _unref(errorMessage),
              fullscreen: _unref(fullscreen)
            }, null, 8, ["preview-file", "preview-type", "file-blob-source", "text-content", "loading-file", "file-error-text", "fullscreen"]),
            _createVNode(TextBase, {
              type: "paragraph",
              weight: "bold",
              text: previewFileFullscreen.value.name
            }, null, 8, ["text"]),
            _createVNode(FileMetaInfo, { "directory-item": previewFileFullscreen.value }, null, 8, ["directory-item"])
          ]))
        : _createCommentVNode("", true)
    ], 2)
  ], 4))
}
}

})